<template>
  <div class="ecg-x-panel">
    <svg ref="svg" :width="_size.width" :height="_size.height">
      <clipPath id="panel-content-clip">
        <rect />
      </clipPath>
    </svg>
    <scatter-tooltip ref="tooltip" :data="highlightedData" v-if="highlightedData"></scatter-tooltip>
    <div class="legendContainer">
      <div class="legend-item" v-for="(lead,index) in this.selectedLeads" :key="lead.key">
        <div
          class="legend-point"
          :style="{ backgroundColor: colorsUsed[selectedLeads[index].match(/\d+/)[0]-1] }"/>
        <div class="legend-text">{{ lead }}</div>
      </div>
    </div>
    <div v-if="!selectedLeads.length"> 
      <div class="no-leads-selected" v-if="!selectedLeads.length">
          <div class="title">No leads selected</div>
           <div class="subtitle">Select a lead on the top left corner to visualize the ECG</div>
        </div>
    </div>
  </div>
</template>

<script>
import d3 from '@/utils/d3importer'
import widgetContent from '@/mixins/widgetContent'
import ScatterTooltip from '@/components/commons/ScatterTooltip'
import tippy, { followCursor } from 'tippy.js';
import 'tippy.js/dist/tippy.css'; // optional for styling

export default {
  name: "MultiEcg",
  mixins: [widgetContent],
  components: { ScatterTooltip },
  props: {
    data: Array,
    qt: Number,
    qrs: Number,
    ton: Number,
    width: Number,
    height: Number,
    selectedLeads: Array,
    leadOptions: Array,
    brushMode: String,
    highlightedLine: Object,
    forcedMargin: Object
  },
  model: {
    prop: 'highlightedLine',
    event: 'change'
  },
  data() {
    return {
      highlightedData: null,
    }
  },
  beforeDestroy() {
    this.highlightedData = null
    if (this.lastTippy) {
      this.lastTippy.destroy()
    }
  },
  mounted() {
    this.x = d3.scaleLinear()
    this.y = d3.scaleLinear()
    // creates bottom horizontal and vertical axis for the given scale
    this.axisFuncs = {
      x: d3.axisBottom(this.x),
      y: d3.axisLeft(this.y)
    }
    if (this.data) {
      this.updateChart()
    }
  },
  methods:{
    updateChart(){
      let allLines = this.ecgLines[0].arr.flat().flat()
      
      let x = this.x.range([0,this.contentSize.width]).domain(d3.extent(allLines,(d)=>+d.dt))
      let y = this.y.range([this.contentSize.height,0]).domain(d3.extent(allLines,(d)=>+d.value))

      let ext = y.domain()[1] - y.domain()[0]
      y.domain([y.domain()[0] - ext * 0.1, y.domain()[1] + ext * 0.1])

      if (navigator.userAgent.toLowerCase().indexOf('firefox') > -1) {
        this.$refs.svg.createSVGPoint = null
      }
      let svg = d3.select(this.$refs.svg)
      let root = svg.selectAll('.root').data([null])
      root = root.merge(root.enter().append('g').attr('class', 'root'))

      this.updateAxes()
      
      let content = root.selectAll('.content').data([null])
      let contentEnter = content.enter().append('g').attr('class', 'content').attr('clip-path', 'url(#panel-content-clip)')
      contentEnter.append('rect').attr('class', 'background').on('mousemove', this.highlight).on('click', this.select)
      content = content.merge(contentEnter)
      content.attr('transform', `translate(${this.margin.left},${this.margin.top})`)
      content.select('.background')
        .attr('width', this.size.width - this.margin.left - this.margin.right)
        .attr('height', this.size.height - this.margin.top - this.margin.bottom)

      let clippath = d3.select(this.$refs.svg).select('#panel-content-clip')
      clippath.select('rect')
        .attr('width', this.size.width - this.margin.left - this.margin.right)
        .attr('height', this.size.height - this.margin.top - this.margin.bottom)

      this.content = content

      this.updateLines()
      
      content.call(d3.zoom()
        .scaleExtent([1, 20])
        .translateExtent([[0, 0], [this.size.width, this.size.height]])
        .on("zoom", () => {
          // if(!this.axes) return 
          if (this.x.invert) {
            this.axisFuncs.x.scale(d3.event.transform.rescaleX(this.x))
            // this.axisFuncs.y.scale(d3.event.transform.rescaleY(this.y))
          } else {
            this.axisFuncs.x.scale(this.x.copy().range([this.margin.left, this.size.width - this.margin.right].map(d => d3.event.transform.applyX(d))))
          }
          this.updateAxes()
          this.updateLines()
          // this.drawSpider(this.highlightedData,{transition: false})
          //Esto rasca "bastante" con mas de 500 puntos...
          // let points = content.selectAll('.point')
          // points
          //   .attr('transform',(d)=>`translate(${this.layoutX(d)},${this.layoutY(d)})`)
        })
      )
      if(!this.selectedLeads.length) {
        d3.selectAll(".brush-interval").remove()
      }
    },
    updateAxes() {
      let root = d3.select(this.$refs.svg).select('.root')
      let xAxis = root.selectAll('.x-axis').data([null])
      let xAxisEnter = xAxis.enter().append('g').attr('class', 'axis x-axis')
      xAxisEnter.append('text').attr('class', 'label').text('Time (s)')

      xAxis = xAxis.merge(xAxisEnter)
      xAxis.call(this.axisFuncs.x).attr('transform', `translate(${this.margin.left},${this.contentSize.height})`);
      xAxis.select('.label').attr("transform", `translate(${this.contentSize.width / 2},${this.margin.bottom - 10})`)

      let yAxis = root.selectAll('.y-axis').data([null])
      let yAxisEnter = yAxis.enter().append('g').attr('class', 'axis y-axis')
      yAxisEnter.append('text').attr('class', 'label').text('V (mV)')

      yAxis = yAxis.merge(yAxisEnter)
      yAxis.call(this.axisFuncs.y).attr('transform', `translate(${this.margin.left},${this.margin.top})`);
      yAxis.select('.label').attr("transform", `translate(-${this.margin.left - 30},${this.contentSize.height / 2}) rotate(-90)`)

      let grid = root.selectAll('.grid').data([null])
      let gridEnter = grid.enter().append('g').attr('class', 'grid')
      grid = grid.merge(gridEnter)

      let xlines = grid.selectAll('.x-line').data(this.axisFuncs.x.scale().ticks(8))
      xlines.enter().append('line').attr('class', 'x-line')
      xlines.exit().remove()

      let ylines = grid.selectAll('.y-line').data(this.axisFuncs.y.scale().ticks(6))
      ylines.enter().append('line').attr('class', 'y-line')
      ylines.exit().remove()

      grid.attr("transform", `translate(${this.margin.left},${this.margin.top})`)
      grid.selectAll('.y-line').attr('x2', this.contentSize.width).attr('y1', (d) => this.axisFuncs.y.scale()(d)).attr('y2', (d) => this.axisFuncs.y.scale()(d))
      grid.selectAll('.x-line').attr('y2', this.contentSize.height).attr('x1', (d) => this.axisFuncs.x.scale()(d)).attr('x2', (d) => this.axisFuncs.x.scale()(d))
    },
    select(data) {
      this.selectedData = data
      this.updateLines()
      this.highlightedData = data
      this.$nextTick(this.placeTooltip)
      this.$emit('change', data)
    },
    highlight(data) {
      this.highlightedData = data
      this.$nextTick(this.placeTooltip)
    },
    updateLines(){
      let items
    // we keep this code in case we want to use the tooltip
      /* if(this.selectedLeads == "Stack") {
        items = this.content.selectAll('.line').data(this.ecgLines,(d)=>d.id)
        let self = this
        items.exit().remove()
        let itemsEnter = items.enter().append('g').attr('class','line')
        itemsEnter.append('path').attr('class','path')
        itemsEnter.append('path').attr('class','ghost')
        itemsEnter.on('mouseover',(d,i)=>{
          this.highlight(this.data.find((dd)=>dd.subject.id === d.id))
        })
        .on('click',function(d,i){
          self.select(self.data.find((dd)=>dd.subject.id === d.id))
          d3.select(this).raise()
        })
        items = items.merge(itemsEnter)

        let line = d3.line()
          .x((d) => this.axisFuncs.x.scale()(+d.dt))
          .y((d) => this.axisFuncs.y.scale()(+d.value));
        
        items.select('.ghost')
          .attr('d',(d)=>line(d.arr))
          .attr('stroke','#fa0')
          .attr('stroke-width',10)
          .attr('opacity',0)
          .style('cursor','pointer')
          
        items.select('.path')
          .attr('d',(d)=>line(d.arr))
          .attr('stroke',(d,i)=>{
            return this.color(d.id)
          })
          .attr('opacity',(d)=>{
            if(this.selectedData){
              if(this.selectedData.subject.id === d.id) return 1
              else return 0.2
            } else {
              return 1
            }
          })
          .attr('stroke-width',(d)=>{
            if(this.selectedData && this.selectedData.subject.id === d.id) return 3
            else return 2
          })
      } else { */
          this.content.selectAll('.line').remove() // remove old items
          items = this.content.selectAll('.line').data(this.ecgLines[0].arr, (d)=>d.id)
          items = items.merge(items.enter().append('path').attr('class','line'))
          let line = d3.line()
            .x((d) => this.axisFuncs.x.scale()(+d.dt))
            .y((d) => this.axisFuncs.y.scale()(+d.value));
          items
            .attr('d',line)
            .attr('stroke-width',2.5)
            .attr('stroke',(d,i)=>{
              let leadIdx = this.selectedLeads[i].match(/\d+/)[0]
              return this.colorsUsed[leadIdx-1]
        })//}
    },
    placeTooltip() {
      if (this.lastTippy) {
        this.lastTippy.destroy()
      }
      if (!this.$refs.tooltip) return
      // const target = d3.select(this.$refs.svg).select('.content').selectAll('.item').filter((d)=>d===this.highlightedData).node()
      const target = d3.select(this.$refs.svg).select('.content').node()
      const tooltip = this.$refs.tooltip.$el

      this.lastTippy = tippy(target, {
        content: tooltip,
        showOnCreate: true,
        // trigger: 'click',
        // customClass: 'tooltip-custom',
        maxWidth: 340,
        interactive: true,
        appendTo: document.body,
        plugins: [followCursor],
        followCursor: true
        // interactiveDebounce: 75,
      });
    },
    measureInterval() {
      d3.selectAll(".brush-interval").remove()
      let start
      let end
      if (this.brushMode == 'qt') {
        start = this.ton
        end = this.ton+this.qt
      } else if (this.brushMode == 'qrs') {
        start = this.ton
        end = this.ton+this.qrs
      }
      
      let svg = d3.select(this.$refs.svg)
      let currentX = this.axisFuncs.x.scale()
      let currentY = this.axisFuncs.y.scale()
      let extent = [[currentX.range()[0]+this.margin.left, currentY.range()[1]], [currentX.range()[1]+this.margin.left, currentY.range()[0]]]
      let brush = d3.brushX().extent(extent)

      let intervalSelection = [this.x(start) + this.margin.left, this.x(end) + this.margin.left]
      let brushEnter = svg.append("g").attr("class", "brush-interval").call(brush).call(brush.move, intervalSelection)
      let getYsfromXs = (sel) => {
        let exts = this.ecgLines.map((d) => {
          let filtered = d.arr.filter((dd) => currentX(dd.dt) > sel[0] && currentX(dd.dt) < sel[1])
          return d3.extent(filtered, (dd) => dd.value)
        })
        return d3.extent(exts.flat()).map((d) => currentY(d)).map((d) => d ? d : 0)
      }
      let drawBrushDetails = (sel) => {
        let brushArea = svg.selectAll('.brush-interval').data((sel && this.brushMode) ? [this.brushMode] : [], (d) => d)
        //brushArea.exit().remove()
        let brushAreaEnter = brushArea.enter().append("g")
          .attr("class", "brush-interval " + this.brushMode)

        let t = brushAreaEnter.append('g').attr('class', 'detail top')
        t.append('line').attr('stroke', '#222').attr('stroke-dasharray', '1 4')
        t.append('text').attr('dx', 5).attr('dy', -5)
        let b = brushAreaEnter.append('g').attr('class', 'detail bottom')
        b.append('line').attr('stroke', '#222').attr('stroke-dasharray', '1 4')
        b.append('text').attr('dx', 5).attr('dy', -5)
        let l = brushAreaEnter.append('g').attr('class', 'detail left')
        l.append('line').attr('stroke', 'rgba(0,0,0,0.5)').attr('stroke-width', 1).attr('stroke-dasharray', '10')
        l.append('text').attr('dx', 5)
        let r = brushAreaEnter.append('g').attr('class', 'detail right')
        r.append('line').attr('stroke', 'rgba(0,0,0,0.5)').attr('stroke-width', 1).attr('stroke-dasharray', '10')
        r.append('text').attr('dx', 5)

        if (!sel) return
        let xRange = sel.map((d) => d)
        let yRange = getYsfromXs(sel)
        let x1 = xRange[0] - this.margin.left
        let x2 = xRange[1] - this.margin.left

        brushArea = brushArea.merge(brushAreaEnter)
        brushArea.select('.top').select('line').attr('x1', extent[0][0]).attr('x2',extent[1][0]).attr('y1', yRange[0]).attr('y2', yRange[0])
        brushArea.select('.top').select('text').text(currentY.invert(yRange[0]).toFixed(2) + 'mV').attr('transform', `translate(${xRange[1]},${yRange[0]})`)
        brushArea.select('.bottom').select('line').attr('x1', extent[0][0]).attr('x2', extent[1][0]).attr('y1', yRange[1]).attr('y2', yRange[1])
        brushArea.select('.bottom').select('text').text(currentY.invert(yRange[1]).toFixed(2) + 'mV').attr('transform', `translate(${xRange[1]},${yRange[1]})`)

        brushArea.select('.left').select('line').attr('x1',xRange[0]).attr('x2',xRange[0]).attr('y1', extent[0][1]).attr('y2', extent[1][1])
        brushArea.select('.left').select('text').text(Math.floor(currentX.invert(x2) * 1000) + 'ms').attr('transform', `translate(${xRange[1]},${extent[1][1]})`)
        brushArea.select('.right').select('line').attr('x1', xRange[1]).attr('x2', xRange[1]).attr('y1', extent[0][1]).attr('y2', extent[1][1])
        brushArea.select('.right').select('text').text(Math.floor(currentX.invert(x1) * 1000) + 'ms').attr('transform', `translate(${xRange[0]},${extent[1][1]})`)
        if (this.brushMode == 'qt') {
          this.$emit('modifyQt', [Number(currentX.invert(x1)),Number(currentX.invert(x2))])
        } else if (this.brushMode == 'qrs') {
            this.$emit('modifyQrs', [Number(currentX.invert(x1)),Number(currentX.invert(x2))])
        }
      }
      if (brush) {
        brush
          .on("end", (e) => {
            drawBrushDetails(d3.event.selection)
          })
          .on("brush", (e) => {
            drawBrushDetails(d3.event.selection)
          })

        //brushEnter.selectAll('.selection').attr('fill', color).attr('stroke', 'none')
        brushEnter.selectAll('.handle--w').style('height', this.contentSize.height)
        brushEnter.selectAll('.handle--e').style('height', this.contentSize.height)
      }
      drawBrushDetails(intervalSelection)
    },
  },
  computed: {
    _size() {
      let w = this.width || this.size.width
      let h = this.height || this.size.height
      let controlsWidth = this.controls ? h : 0
      return {
        width: w - controlsWidth,
        height: h
      }
    },
    colorsUsed() {
      // Generate colors for all available leads
      let availableLeads = Array.from(this.leadOptions.keys())
      return availableLeads.map(i => {return d3.schemePaired[i]})
    },
    contentSize() {
      return {
        width: Math.max(0, this._size.width - this.margin.left - this.margin.right),
        height: Math.max(0, this._size.height - this.margin.top - this.margin.bottom)
      }
    },
    margin() {
      let m = { left: 0, top: 0, bottom: 0, right: 0 }
      m.bottom = 40
      m.left = 70
      if (this.forcedMargin) {
        Object.keys(this.forcedMargin).forEach((k) => m[k] = this.forcedMargin[k])
      }
      return m

    },
    ecgLines() {
      if (this.data) {
        return this.data.map(d => {
          let id = d.subject.id
          if (d.sparkline) {
            let leadLine
            let arr = []
            this.selectedLeads.forEach((lead) => {
              let leadIdx = d.sparkline.headers.findIndex(i => { return lead === i })
              leadLine = d.sparkline.rows.map((r,i) => {
                return {dt: r[0], value: r[leadIdx]}
              })
              arr.push(leadLine)
            })
            return {id: id, arr: arr}
          }
          return { id: id, arr: [] }
        })
      }
    },
  },
  watch: {
    ecgLines() {
      this.updateChart()
      
      // For the legend: sort leads if necessary
      if(this.selectedLeads.length) {
        if(!!this.selectedLeads.reduce((n, item) => n !== false && item >= n && item) == false) {this.selectedLeads.sort()}
      }
    },
    _size() {
      if (this.data) {
        this.updateChart()
        this.measureInterval()
      }
    },
    brushMode() {
      this.measureInterval()
    },
    highlightedLine() {
      this.selectedData = this.highlightedLine
      this.updateLines()
    }
  }
}
</script>

<style scoped lang="stylus">
.ecg-x-panel
  position relative
  // overflow hidden //no vale para los tooltips que se salen!
  //width 100%
  height 100%
  display flex

  svg
    // position: absolute;
    >>> .root
      .line
        // pointer-events none
        fill: none
      .grid
        line
          stroke-width 0.2px
          stroke #444 //#ffffff
      .axis
        .tick
          line
            stroke-width 0.2px
          text
            fill #222 //#aaa
        .domain
          stroke-width 0px
        .label
          font-size 12px
          fill #444
      .content
        .background
          fill: #fff
          opacity 0
    >>> .brush-details
      .detail
        text
          fill #444 //
          font-size 12px
  >>> .current
    pointer-events none
    position absolute
    top 0
    left 0
    .circle
      display none
    .inner-circle
      position absolute
      width 8px
      height 8px
      border-radius 4px
      background: #f00;
      transform: translate(-50%,-50%)
      box-shadow 0 0 5px rgba(0,0,0,0.2)
    .tooltip
      position absolute
      bottom 20px
      left 0
      transform translate(-50%,0)
      white-space nowrap
      // border solid 1px
      background #fff
      padding 2px 7px
      box-shadow 0 0 5px rgba(0,0,0,0.2)
      m-tooltip-tip()

    &.large
      .circle
        display block
        position absolute
        width 20px
        height 20px
        border-radius 10px
        background: #fff;
        transform: translate(-50%,-50%);
        box-shadow 0 0 5px rgba(0,0,0,0.2)
      .inner-circle
        position absolute
        width 6px
        height 6px
        border-radius 3px
        background: #f00;
        transform: translate(-50%,-50%)

  .legendContainer {
    display: flex
    flex-flow: column wrap
    margin-top: 10px
    margin-left: 15px
  }

  .legend-item {
    height: 35px
  }
  .legend-text {
    color: $shuttle-gray
    display: flex
    color: black
    font-size: 12px
  }

  .legend-point {
    color: $shuttle-gray
    height: 10px
    border-radius: 10px
  }
  .no-leads-selected
      position: absolute
      text-align: center
      top: 40%
      left: 0
      width: 100%
      height: 100%
      background: rgba(255, 255, 255, 1)
      color: #000
      .title
        m-font-size(18)
        margin-bottom: 20px
        font-weight: $fw-bold

      .subtitle
        m-font-size(14)


</style>